import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface TAConsignorMappingDto {
    id: number;
    companyNo: string;
    consignorCode: string;
    externalConsignorId: string;
    isDefault: boolean;
    description: string | null;
    updatedBy?: string;
    lastUpdate?: string;
    createdBy?: string;
    created?: string;
}

export interface CreateTAConsignorMappingRequest {
    companyNo: string;
    consignorCode: string;
    externalConsignorId: string;
    isDefault: boolean;
    description: string | null;
}

export interface UpdateTAConsignorMappingRequest {
    consignorCode?: string;
    externalConsignorId?: string;
    isDefault?: boolean;
    description?: string | null;
}

export interface BulkUpdateTAConsignorMappingRequest {
    companyNo: string;
    consignorCode: string;
    externalConsignorId: string;
    isDefault: boolean;
    description: string | null;
}

export interface BulkDeleteResult {
    deletedCount: number;
    requestedCount: number;
    notFoundIds: number[];
}

class TAConsignorMappingService {
    private baseUrl = `${API_BASE_URL}/ta-consignor-mappings`;

    async getByConsignorCode(consignorCode: string): Promise<TAConsignorMappingDto | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${encodeURIComponent(consignorCode)}`);
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) return null;
            throw error;
        }
    }

    async getCurrent(): Promise<TAConsignorMappingDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return await response.json();
    }

    async getAll(): Promise<TAConsignorMappingDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return await response.json();
    }

    async create(request: CreateTAConsignorMappingRequest): Promise<TAConsignorMappingDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async update(consignorCode: string, request: UpdateTAConsignorMappingRequest): Promise<TAConsignorMappingDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${encodeURIComponent(consignorCode)}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async bulkUpdate(requests: BulkUpdateTAConsignorMappingRequest[]): Promise<TAConsignorMappingDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async delete(consignorCode: string): Promise<void> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${encodeURIComponent(consignorCode)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
    }

    async bulkDelete(ids: number[]): Promise<BulkDeleteResult> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async search(searchTerm: string): Promise<TAConsignorMappingDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }
}

export const taConsignorMappingService = new TAConsignorMappingService();
